/**
 * Mirror Guardian (P5 Wave 4 §2) — the honesty governor for the Mirror, the
 * feature that lets bereaved families converse with a departed member's
 * persona. These four numbers decide, per reply, whether the Mirror answers
 * directly, answers with caveats (hedges), declines for lack of evidence, or
 * revises a reply that doesn't sound enough like the person. Every save changes
 * what real grieving families hear, so the page mirrors the backend bounds
 * client-side (blocking a non-monotonic or out-of-range save before submit) and
 * routes the write through QuillActionConfirm with copy that names that impact.
 *
 * GET / on load → the sliders + live decline→hedge→answer band. Save validates,
 * confirms, PUTs; a backend 400 GUARDIAN_BOUNDS (should never happen once the
 * client guard passes, but is the source of truth) is surfaced verbatim. Revert
 * discards edits back to the loaded config.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import { useQuillHealth } from '../QuillHealthContext';
import EmptyState from '../../common/EmptyState';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors, typography } from '../../../theme/designTokens';
import { QuillApiError } from '../../../services/quillAdminService';
import { mirrorGuardianApi, GuardianConfig } from './api';
import GuardianBandVisualization from './GuardianBandVisualization';
import {
  GuardianDraft,
  validateGuardianDraft,
  isGuardianDraftValid,
  draftFromConfig,
  isDraftDirty,
} from './guardianValidation';

type Severity = 'success' | 'error';

const fmtUnit = (n: number) => n.toFixed(2);

const MirrorGuardianPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [config, setConfig] = useState<GuardianConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState<GuardianDraft | null>(null);
  const [baseline, setBaseline] = useState<GuardianDraft | null>(null);
  const [reason, setReason] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [boundsError, setBoundsError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: Severity }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    mirrorGuardianApi
      .getConfig()
      .then((c) => {
        setConfig(c);
        const d = draftFromConfig(c);
        setDraft(d);
        setBaseline(d);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Failed to load guardian config.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const errors = useMemo(() => (draft ? validateGuardianDraft(draft) : {}), [draft]);
  const valid = draft ? isGuardianDraftValid(draft) : false;
  const dirty = draft && baseline ? isDraftDirty(draft, baseline) : false;
  const reasonValid = reason.trim().length > 0;
  const canSave = Boolean(dirty && valid && reasonValid);

  const patch = (p: Partial<GuardianDraft>) => {
    setBoundsError(null);
    setDraft((prev) => (prev ? { ...prev, ...p } : prev));
  };

  const handleRevert = () => {
    if (baseline) setDraft(baseline);
    setReason('');
    setBoundsError(null);
  };

  const handleRequestSave = () => {
    // Defensive: the button is already disabled when invalid, but never open the
    // confirm on an invalid draft.
    if (!canSave) return;
    setBoundsError(null);
    setConfirmOpen(true);
  };

  const handleConfirmSave = () => {
    if (!draft) return;
    setSaving(true);
    mirrorGuardianApi
      .updateConfig({ ...draft, reason: reason.trim() })
      .then((c) => {
        setConfig(c);
        const d = draftFromConfig(c);
        setDraft(d);
        setBaseline(d);
        setReason('');
        setConfirmOpen(false);
        setSnackbar({
          open: true,
          message: 'Mirror Guardian settings saved — the live Mirror now uses these thresholds.',
          severity: 'success',
        });
      })
      .catch((e) => {
        setConfirmOpen(false);
        if (e instanceof QuillApiError && e.code === 'GUARDIAN_BOUNDS') {
          // Backend is the source of truth; surface its message distinctly.
          setBoundsError(e.message || 'The backend rejected these bounds (GUARDIAN_BOUNDS).');
          setSnackbar({ open: true, message: 'Rejected: guardian bounds invalid.', severity: 'error' });
        } else {
          setSnackbar({
            open: true,
            message: e instanceof Error ? e.message : 'Failed to save guardian config.',
            severity: 'error',
          });
        }
      })
      .finally(() => setSaving(false));
  };

  // ── Header (shared pattern: 48px icon tile + Playfair title + subtitle) ──
  const header = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <BalanceIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: typography.fonts.display,
              fontSize: '1.75rem',
              fontWeight: 700,
              color: colors.gray[900],
            }}
          >
            Mirror Guardian
          </Typography>
          <Typography
            sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}
          >
            Honesty thresholds that govern what the Mirror says to bereaved families.
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● {status === 'up' ? 'Connected' : status}
      </Typography>
    </>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {header}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (loadError || !draft || !config) {
    return (
      <Box sx={{ p: 3 }}>
        {header}
        <EmptyState
          icon="error"
          title="Couldn't load Mirror Guardian"
          description={loadError || 'The guardian configuration could not be loaded.'}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {header}

      {boundsError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setBoundsError(null)}>
          <strong>Guardian bounds rejected by the backend:</strong> {boundsError}
        </Alert>
      )}

      {/* Slider card */}
      <Box
        sx={{
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: '16px',
          p: { xs: 2, sm: 3 },
          mb: 3,
          maxWidth: 760,
        }}
      >
        {/* Live band */}
        <Typography
          sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}
        >
          Evidence band
        </Typography>
        <GuardianBandVisualization
          decline={draft.evidence_floor_decline}
          hedge={draft.evidence_band_hedge}
        />

        <Divider sx={{ my: 3 }} />

        {/* Decline floor */}
        <SliderRow
          label="Decline floor"
          help="Below this evidence score, the Mirror declines to answer rather than guess."
          value={draft.evidence_floor_decline}
          min={0}
          max={1}
          step={0.01}
          displayValue={fmtUnit(draft.evidence_floor_decline)}
          error={errors.evidence_floor_decline}
          ariaLabel="Decline floor"
          onChange={(v) => patch({ evidence_floor_decline: v })}
        />

        {/* Hedge threshold */}
        <SliderRow
          label="Hedge threshold"
          help="Between the decline floor and this value the Mirror answers, but hedges. Must be at or above the decline floor."
          value={draft.evidence_band_hedge}
          min={0}
          max={1}
          step={0.01}
          displayValue={fmtUnit(draft.evidence_band_hedge)}
          error={errors.evidence_band_hedge}
          ariaLabel="Hedge threshold"
          onChange={(v) => patch({ evidence_band_hedge: v })}
        />

        {/* Likeness revision floor */}
        <SliderRow
          label="Likeness revision floor"
          help="If a drafted reply scores below this likeness to the member, the Mirror revises it before sending."
          value={draft.likeness_revision_floor}
          min={0}
          max={1}
          step={0.01}
          displayValue={fmtUnit(draft.likeness_revision_floor)}
          error={errors.likeness_revision_floor}
          ariaLabel="Likeness revision floor"
          onChange={(v) => patch({ likeness_revision_floor: v })}
        />

        {/* History window */}
        <SliderRow
          label="History window"
          help="How many prior conversation turns the Mirror considers. Whole number, 1–50."
          value={draft.max_history_turns}
          min={1}
          max={50}
          step={1}
          displayValue={`${draft.max_history_turns} turns`}
          error={errors.max_history_turns}
          ariaLabel="History window"
          onChange={(v) => patch({ max_history_turns: Math.round(v) })}
        />

        <Divider sx={{ my: 3 }} />

        {/* Reason (change-log) */}
        <TextField
          label="Reason for change"
          placeholder="Recorded in the change log (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
          error={dirty && !reasonValid}
          helperText={dirty && !reasonValid ? 'A reason is required to save.' : ' '}
          inputProps={{ 'aria-label': 'Reason for change' }}
        />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleRequestSave}
            disabled={!canSave || saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Save settings
          </Button>
          <Button
            variant="outlined"
            onClick={handleRevert}
            disabled={!dirty || saving}
            sx={{ textTransform: 'none' }}
          >
            Revert
          </Button>
          {dirty && !valid && (
            <Typography variant="caption" sx={{ alignSelf: 'center', color: colors.error.main, fontWeight: 600 }}>
              Fix the highlighted values before saving.
            </Typography>
          )}
        </Box>
      </Box>

      <QuillActionConfirm
        open={confirmOpen}
        title="Save Mirror Guardian settings"
        message={
          <>
            These thresholds shape what the Mirror says to grieving families — declining, hedging, or
            answering, and when a reply is revised to sound more like the departed member. Saving
            applies them to the live Mirror immediately (production).
          </>
        }
        caption={
          baseline ? (
            <Box component="span">
              <ChangeLine label="Decline floor" from={fmtUnit(baseline.evidence_floor_decline)} to={fmtUnit(draft.evidence_floor_decline)} />
              <ChangeLine label="Hedge threshold" from={fmtUnit(baseline.evidence_band_hedge)} to={fmtUnit(draft.evidence_band_hedge)} />
              <ChangeLine label="Likeness floor" from={fmtUnit(baseline.likeness_revision_floor)} to={fmtUnit(draft.likeness_revision_floor)} />
              <ChangeLine label="History window" from={`${baseline.max_history_turns}`} to={`${draft.max_history_turns}`} />
            </Box>
          ) : undefined
        }
        confirmLabel="Save settings"
        pendingLabel="Saving…"
        loading={saving}
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────

interface SliderRowProps {
  label: string;
  help: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  error?: string;
  ariaLabel: string;
  onChange: (v: number) => void;
}

const SliderRow: React.FC<SliderRowProps> = ({
  label,
  help,
  value,
  min,
  max,
  step,
  displayValue,
  error,
  ariaLabel,
  onChange,
}) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography
        sx={{ fontFamily: typography.fonts.mono, fontWeight: 700, color: error ? colors.error.main : colors.deepTeal[600] }}
      >
        {displayValue}
      </Typography>
    </Box>
    <Slider
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(_e, v) => onChange(Array.isArray(v) ? v[0] : v)}
      aria-label={ariaLabel}
      sx={{
        color: error ? colors.error.main : colors.deepTeal[500],
        '& .MuiSlider-thumb': { width: 18, height: 18 },
      }}
    />
    <Typography variant="caption" sx={{ color: error ? colors.error.main : 'text.secondary', display: 'block' }}>
      {error || help}
    </Typography>
  </Box>
);

const ChangeLine: React.FC<{ label: string; from: string; to: string }> = ({ label, from, to }) => {
  const changed = from !== to;
  return (
    <Box component="span" sx={{ display: 'block' }}>
      <strong>{label}:</strong>{' '}
      {changed ? (
        <>
          <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
            {from}
          </Box>{' '}
          → <Box component="span" sx={{ fontWeight: 700 }}>{to}</Box>
        </>
      ) : (
        <Box component="span" sx={{ opacity: 0.7 }}>{to} (unchanged)</Box>
      )}
    </Box>
  );
};

export default MirrorGuardianPage;
